class LookOfTheDay extends HTMLElement {
  constructor() {
    super();

    this.dots = this.querySelectorAll(".look-of-the-day__dot");
    this.products = this.querySelectorAll("look-of-the-day-product-container");
    this.addToCartButton = this.querySelector(
      ".look-of-the-day__form-submit button"
    );
    this.totalPriceElement = this.querySelector(
      ".look-of-the-day__form-total .price--reduced"
    );
    this.totalCompareAtPriceElement = this.querySelector(
      ".look-of-the-day__form-total .price--compare"
    );

    this.discountPercentage = this.getAttribute("data-discount_percentage");
  }

  connectedCallback() {
    this.#setEventListeners();
  }

  #getTotalCompareAtPrice() {
    return Array.from(
      this.querySelectorAll(".look-of-the-day__product--selected")
    ).reduce((acc, product) => {
      return acc + parseFloat(product.getPrice());
    }, 0);
  }

  updateTotalPriceElements() {
    this.totalPriceElement.innerHTML =
      (
        (this.#getTotalCompareAtPrice() / 100) *
        this.discountPercentage
      ).toFixed(2) + "&nbsp;€";
    this.totalCompareAtPriceElement.innerHTML =
      (this.#getTotalCompareAtPrice() / 100).toFixed(2) + "&nbsp;€";
  }

  #setEventListeners() {
    this.addToCartButton.addEventListener("click", () => {
      this.#addProductsSequentially();
    });

    this.dots.forEach((dot, dotIndex) => {
      dot.addEventListener("mouseenter", () => {
        this.products.forEach((product, productIndex) => {
          if (productIndex !== dotIndex) {
            product.disable();
          } else {
            product.enable();
          }
        });
      });

      dot.addEventListener("mouseleave", () => {
        this.products.forEach((product, productIndex) => {
          product.enable();
        });
      });
    });
  }

  async #addProductsSequentially() {
    const selectedProducts = Array.from(
      this.querySelectorAll(".look-of-the-day__product--selected")
    ).reverse();

    // Disable button during the process
    this.addToCartButton.setAttribute("disabled", "disabled");
    this.addToCartButton.setAttribute("aria-busy", "true");

    try {
      // Add products one by one silently (sans déclencher d'événements)
      for (const product of selectedProducts) {
        const productForm = new FormData(
          product.querySelector(".look-of-the-day__product-form")
        );
        const response = await fetch(
          `${window.themeVariables.routes.cartAddUrl}.js`,
          {
            body: productForm,
            method: "POST",
            headers: {
              "X-Requested-With": "XMLHttpRequest",
            },
          }
        );

        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(
            responseJson["description"] || "Error adding product to cart"
          );
        }
      }

      // Une fois tous les produits ajoutés, on récupère le contenu final du panier
      const cartResponse = await fetch(
        `${window.themeVariables.routes.cartUrl}.js`
      );
      const cartContent = await cartResponse.json();

      // Redirection si nécessaire
      if (
        window.themeVariables.settings.cartType === "page" ||
        window.themeVariables.settings.pageType === "cart"
      ) {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      // On déclenche les événements une seule fois à la fin
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { cart: cartContent },
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            openMiniCart:
              window.themeVariables.settings.cartType === "drawer" &&
              this.closest(".drawer") === null,
          },
        })
      );

      // Show success notification
      this.dispatchEvent(
        new CustomEvent("cart-notification:show", {
          bubbles: true,
          cancelable: true,
          detail: { status: "success" },
        })
      );
    } catch (error) {
      // Show error notification
      this.dispatchEvent(
        new CustomEvent("cart-notification:show", {
          bubbles: true,
          cancelable: true,
          detail: {
            status: "error",
            error: error.message,
          },
        })
      );
    } finally {
      // Re-enable button
      this.addToCartButton.removeAttribute("disabled");
      this.addToCartButton.removeAttribute("aria-busy");
    }
  }
}

customElements.define("look-of-the-day", LookOfTheDay);

class LookOfTheDayProductContainer extends HTMLElement {
  constructor() {
    super();
    this.parentLookOfTheDay = this.closest("look-of-the-day");
    this.products = this.querySelectorAll("look-of-the-day-product");
    this.disabled = false;
  }

  connectedCallback() {
    this.#setEventListeners();
  }

  #setEventListeners() {
    // Update the selected color variant
    this.products.forEach((product) => {
      product
        .querySelectorAll(".look-of-the-day__color-picker-item")
        .forEach((item, index) => {
          item.addEventListener("click", () => {
            // Update the selected color variant
            this.products.forEach((product, productIndex) => {
              product.classList.toggle(
                "look-of-the-day__product--selected",
                productIndex === index
              );
            });
            // Update the total price
            this.parentLookOfTheDay.updateTotalPriceElements();
          });
        });
    });
  }

  disable() {
    this.disabled = true;
    this.classList.add("look-of-the-day__product-container--disabled");
  }

  enable() {
    this.disabled = false;
    this.classList.remove("look-of-the-day__product-container--disabled");
  }
}

customElements.define(
  "look-of-the-day-product-container",
  LookOfTheDayProductContainer
);

class LookOfTheDayProduct extends HTMLElement {
  constructor() {
    super();
    this.parentLookOfTheDay = this.closest("look-of-the-day-product-container");
    this.variantInput = this.querySelector(
      ".look-of-the-day__product-variant-input"
    );
  }

  connectedCallback() {
    this.#setEventListeners();
  }

  #setEventListeners() {
    this.variantInput.addEventListener("change", () => {
      this.parentLookOfTheDay.updateTotalPriceElements();
    });
  }

  getPrice() {
    return this.variantInput.selectedOptions[0].dataset.price;
  }
}

customElements.define("look-of-the-day-product", LookOfTheDayProduct);
